import { Timestamp } from "firebase/firestore";
import { executeQuery } from "src/store";
import { baseService } from "src/store/base.store";
import { aiCoachLevelVocabularyRepository } from "./ai-coach-level-vocabulary.repository";
import {
  AICoachLevelVocabulary,
  UpsertAICoachLevelVocabularyPayload,
} from "./entities/ai-coach-level-vocabulary.entity";
import {
  normalizeLangCode,
  sanitizeLevelVocabularyLevels,
  validateLevelVocabularyPayload,
} from "./utils/level-vocabulary";

const aiCoachLevelVocabularyService = baseService.injectEndpoints({
  endpoints: (builder) => ({
    getAICoachLevelVocabularies: builder.query<(AICoachLevelVocabulary & { id: string })[], void>({
      queryFn: async () => {
        const result = await executeQuery(() =>
          aiCoachLevelVocabularyRepository.find({
            limit: 250,
            orderBy: ["langCode", "asc"],
          } as any)
        );

        if ("error" in result) {
          return { error: result.error } as any;
        }

        return {
          data: (result.data as unknown) as (AICoachLevelVocabulary & { id: string })[],
        };
      },
      providesTags: ["AI_COACH_LEVEL_VOCABULARIES"],
    }),

    upsertAICoachLevelVocabulary: builder.mutation<void, UpsertAICoachLevelVocabularyPayload>({
      queryFn: async (payload) => {
        const validationError = validateLevelVocabularyPayload(payload);
        if (validationError) {
          return { error: { status: "CUSTOM_ERROR", error: validationError } } as any;
        }

        try {
          const normalizedLangCode = normalizeLangCode(payload.langCode);
          await aiCoachLevelVocabularyRepository.setById(
            normalizedLangCode,
            {
              langCode: normalizedLangCode,
              levels: sanitizeLevelVocabularyLevels(payload.levels),
              updatedAt: Timestamp.now(),
              updatedByAdminId: payload.updatedByAdminId,
              updatedByAdminName: payload.updatedByAdminName,
            },
            { merge: true }
          );

          return { data: undefined };
        } catch (error) {
          return {
            error: { status: "CUSTOM_ERROR", error: "Failed to save AI coach level vocabulary." },
          } as any;
        }
      },
      invalidatesTags: ["AI_COACH_LEVEL_VOCABULARIES"],
    }),

    upsertAICoachLevelVocabularies: builder.mutation<void, UpsertAICoachLevelVocabularyPayload[]>({
      queryFn: async (payloads) => {
        if (!Array.isArray(payloads) || payloads.length === 0) {
          return {
            error: { status: "CUSTOM_ERROR", error: "At least one language vocabulary configuration is required." },
          } as any;
        }

        for (const payload of payloads) {
          const validationError = validateLevelVocabularyPayload(payload);
          if (validationError) {
            return { error: { status: "CUSTOM_ERROR", error: validationError } } as any;
          }
        }

        try {
          await Promise.all(
            payloads.map((payload) => {
              const normalizedLangCode = normalizeLangCode(payload.langCode);
              return aiCoachLevelVocabularyRepository.setById(
                normalizedLangCode,
                {
                  langCode: normalizedLangCode,
                  levels: sanitizeLevelVocabularyLevels(payload.levels),
                  updatedAt: Timestamp.now(),
                  updatedByAdminId: payload.updatedByAdminId,
                  updatedByAdminName: payload.updatedByAdminName,
                },
                { merge: true }
              );
            })
          );

          return { data: undefined };
        } catch (error) {
          return {
            error: { status: "CUSTOM_ERROR", error: "Failed to import AI coach level vocabularies." },
          } as any;
        }
      },
      invalidatesTags: ["AI_COACH_LEVEL_VOCABULARIES"],
    }),
  }),
});

export const {
  useGetAICoachLevelVocabulariesQuery,
  useUpsertAICoachLevelVocabularyMutation,
  useUpsertAICoachLevelVocabulariesMutation,
} = aiCoachLevelVocabularyService;
